import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "../utils/api";
import toast from "react-hot-toast";
import { ArrowLeft, Plus, Star } from "lucide-react";

export default function SupplierDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [supplier, setSupplier] = useState(null);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingContact, setEditingContact] = useState(null);
  const [form, setForm] = useState({ first_name: "", last_name: "", position: "", email: "", phone: "", notes: "", is_primary: false });
  const [editForm, setEditForm] = useState({});

  const load = async () => {
    try {
      const [sRes, cRes] = await Promise.all([
        api.get("/suppliers/" + id),
        api.get("/supplier-contacts/supplier/" + id)
      ]);
      setSupplier(sRes.data);
      setEditForm({ name: sRes.data.name, email_general: sRes.data.email_general || "", email_billing: sRes.data.email_billing || "", email_service: sRes.data.email_service || "", phone: sRes.data.phone || "", address: sRes.data.address || "", notes: sRes.data.notes || "", is_active: sRes.data.is_active, customer_number: sRes.data.customer_number || "" });
      setContacts(cRes.data);
    } catch (err) { toast.error("Fehler beim Laden"); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [id]);

  const handleSaveSupplier = async () => {
    try {
      await api.put("/suppliers/" + id, editForm);
      toast.success("Lieferant gespeichert");
      setEditing(false);
      load();
    } catch (err) { toast.error("Fehler beim Speichern"); }
  };

  const handleSubmitContact = async () => {
    try {
      if (editingContact) {
        await api.put("/supplier-contacts/" + editingContact.id, form);
        toast.success("Kontakt gespeichert");
      } else {
        await api.post("/supplier-contacts", { ...form, supplier_id: parseInt(id) });
        toast.success("Kontakt angelegt");
      }
      setShowForm(false);
      setEditingContact(null);
      setForm({ first_name: "", last_name: "", position: "", email: "", phone: "", notes: "", is_primary: false });
      load();
    } catch (err) { toast.error("Fehler beim Speichern"); }
  };

  const handleEditContact = (c) => {
    setEditingContact(c);
    setForm({ first_name: c.first_name || "", last_name: c.last_name || "", position: c.position || "", email: c.email || "", phone: c.phone || "", notes: c.notes || "", is_primary: c.is_primary || false });
    setShowForm(true);
  };

  const handleDeactivateContact = async (cid) => {
    try {
      await api.delete("/supplier-contacts/" + cid);
      toast.success("Kontakt deaktiviert");
      load();
    } catch (err) { toast.error("Fehler"); }
  };

  if (loading) return <div className="p-6 text-sm text-gray-400">Lade...</div>;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <button onClick={() => navigate("/suppliers")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 mb-5">
        <ArrowLeft size={15} /> Zurück
      </button>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 mb-5">
        {editing ? (
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="Name *" value={editForm.name} onChange={e => setEditForm({...editForm, name: e.target.value})}
              className="col-span-2 border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="E-Mail Allgemein" value={editForm.email_general} onChange={e => setEditForm({...editForm, email_general: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="E-Mail Buchhaltung" value={editForm.email_billing} onChange={e => setEditForm({...editForm, email_billing: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="E-Mail Service" value={editForm.email_service} onChange={e => setEditForm({...editForm, email_service: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="Unsere Kundennummer" value={editForm.customer_number} onChange={e => setEditForm({...editForm, customer_number: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <input placeholder="Telefon" value={editForm.phone} onChange={e => setEditForm({...editForm, phone: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
            <textarea placeholder="Adresse" value={editForm.address} onChange={e => setEditForm({...editForm, address: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" rows={2} />
            <textarea placeholder="Notizen" value={editForm.notes} onChange={e => setEditForm({...editForm, notes: e.target.value})}
              className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" rows={2} />
            <div className="col-span-2 flex items-center gap-2">
              <input type="checkbox" id="is_active" checked={editForm.is_active} onChange={e => setEditForm({...editForm, is_active: e.target.checked})} />
              <label htmlFor="is_active" className="text-sm text-gray-700">Aktiv</label>
            </div>
            <div className="col-span-2 flex gap-2">
              <button onClick={handleSaveSupplier} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Speichern</button>
              <button onClick={() => setEditing(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Abbrechen</button>
            </div>
          </div>
        ) : (
          <div>
            <div className="flex items-start justify-between">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">{supplier.name}</h1>
                <span className={"mt-1 inline-block text-xs font-medium px-2.5 py-1 rounded-full " + (supplier.is_active ? "bg-green-50 text-green-700" : "bg-gray-100 text-gray-400")}>
                  {supplier.is_active ? "Aktiv" : "Inaktiv"}
                </span>
              </div>
              <button onClick={() => setEditing(true)} className="text-sm text-primary-600 hover:text-primary-700 font-medium">Bearbeiten</button>
            </div>
            <div className="grid grid-cols-2 gap-4 mt-4 text-sm">
              {supplier.email_general && <div><span className="text-gray-400">E-Mail</span><p className="text-gray-800 mt-0.5">{supplier.email_general}</p></div>}
              {supplier.email_billing && <div><span className="text-gray-400">Buchhaltung</span><p className="text-gray-800 mt-0.5">{supplier.email_billing}</p></div>}
              {supplier.email_service && <div><span className="text-gray-400">Service</span><p className="text-gray-800 mt-0.5">{supplier.email_service}</p></div>}
              {supplier.customer_number && <div><span className="text-gray-400">Unsere Kundennummer</span><p className="text-gray-800 mt-0.5">{supplier.customer_number}</p></div>}
              {supplier.phone && <div><span className="text-gray-400">Telefon</span><p className="text-gray-800 mt-0.5">{supplier.phone}</p></div>}
              {supplier.address && <div className="col-span-2"><span className="text-gray-400">Adresse</span><p className="text-gray-800 mt-0.5 whitespace-pre-line">{supplier.address}</p></div>}
              {supplier.notes && <div className="col-span-2"><span className="text-gray-400">Notizen</span><p className="text-gray-800 mt-0.5 whitespace-pre-line">{supplier.notes}</p></div>}
            </div>
          </div>
        )}
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-semibold text-gray-800">Ansprechpartner</h2>
          <button onClick={() => { setEditingContact(null); setForm({ first_name: "", last_name: "", position: "", email: "", phone: "", notes: "", is_primary: false }); setShowForm(true); }}
            className="flex items-center gap-1.5 text-sm text-primary-600 hover:text-primary-700 font-medium">
            <Plus size={14} /> Hinzufügen
          </button>
        </div>

        {showForm && (
          <div className="border border-gray-200 rounded-xl p-4 mb-4">
            <div className="grid grid-cols-2 gap-3">
              <input placeholder="Vorname *" value={form.first_name} onChange={e => setForm({...form, first_name: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input placeholder="Nachname *" value={form.last_name} onChange={e => setForm({...form, last_name: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input placeholder="Position" value={form.position} onChange={e => setForm({...form, position: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input placeholder="E-Mail" value={form.email} onChange={e => setForm({...form, email: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <input placeholder="Telefon" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500" />
              <textarea placeholder="Notizen" value={form.notes} onChange={e => setForm({...form, notes: e.target.value})}
                className="border border-gray-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 resize-none" rows={2} />
              <div className="col-span-2 flex items-center gap-2">
                <input type="checkbox" id="is_primary" checked={form.is_primary} onChange={e => setForm({...form, is_primary: e.target.checked})} />
                <label htmlFor="is_primary" className="text-sm text-gray-700">Hauptansprechpartner</label>
              </div>
            </div>
            <div className="flex gap-2 mt-3">
              <button onClick={handleSubmitContact} className="bg-primary-600 hover:bg-primary-700 text-white text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Speichern</button>
              <button onClick={() => setShowForm(false)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 text-sm font-semibold px-4 py-2 rounded-xl transition-colors">Abbrechen</button>
            </div>
          </div>
        )}

        {contacts.length === 0 ? (
          <p className="text-sm text-gray-400">Noch keine Ansprechpartner</p>
        ) : (
          <div className="divide-y divide-gray-50">
            {contacts.map(c => (
              <div key={c.id} className="py-3 flex items-start justify-between">
                <div>
                  <div className="flex items-center gap-1.5">
                    <p className="text-sm font-semibold text-gray-900">{c.first_name} {c.last_name}</p>
                    {c.is_primary && <Star size={12} className="text-yellow-400 fill-yellow-400" />}
                  </div>
                  {c.position && <p className="text-xs text-gray-400 mt-0.5">{c.position}</p>}
                  {c.email && <p className="text-xs text-gray-500 mt-0.5">{c.email}</p>}
                  {c.phone && <p className="text-xs text-gray-500">{c.phone}</p>}
                </div>
                <div className="flex gap-2">
                  <button onClick={() => handleEditContact(c)} className="text-xs text-primary-600 hover:text-primary-700 font-medium">Bearbeiten</button>
                  <button onClick={() => handleDeactivateContact(c.id)} className="text-xs text-red-400 hover:text-red-600 font-medium">Entfernen</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
